import * as XLSX from 'xlsx';
import { IConfigOption } from '../types/configOption';
import { ALL_OPTION_TYPES } from '../constants/optionTypes';
import { ALL_INPUT_TYPES } from '../constants/inputTypes';
import { IDisplayCondition } from '../types/displayCondition';

export interface CategoryExportData {
  categoryId?: string;
  categoryName: string;
  options: IConfigOption[];
}

export interface LookupData {
  allCategories: Array<{ id: string; name: string }>;
  allProducts: Array<{ id: string; name: string }>;
}

const getOptionTypeLabel = (value?: number): string => {
  if (value == null) return 'Unknown';
  return ALL_OPTION_TYPES.find((t) => t.value === value)?.label ?? `Type ${value}`;
};

const getInputTypeLabel = (value?: number): string => {
  if (value == null) return '-';
  return ALL_INPUT_TYPES.find((t) => t.value === value)?.label ?? `Input ${value}`;
};

const parseCondition = (conditionJson?: string | null): IDisplayCondition | null => {
  if (!conditionJson) return null;
  try {
    return JSON.parse(conditionJson) as IDisplayCondition;
  } catch {
    return null;
  }
};

const formatDependsOn = (
  condition: IDisplayCondition | null,
  categoryNameById: Map<string, string>,
  optionNameById: Map<string, string>,
): string => {
  if (!condition?.enabled || !condition.dependsOn?.length) return '—';

  return condition.dependsOn
    .map((dep) => {
      const catName = categoryNameById.get(dep.categoryId) ?? dep.categoryId;
      const optNames = dep.optionIds.map((id) => optionNameById.get(id) ?? id);
      return `[${catName}]: ${optNames.join(' OR ')}`;
    })
    .join('\nAND\n');
};

const formatValidForProducts = (
  condition: IDisplayCondition | null,
  productNameById: Map<string, string>,
): string => {
  if (!condition?.enabled) return 'All Products';
  if (!condition.validForProducts?.length) return 'All Products';
  return condition.validForProducts
    .map((id) => productNameById.get(id) ?? id)
    .join(', ');
};

export const exportAllOptionsToExcel = (
  categories: CategoryExportData[],
  lookup?: LookupData,
): void => {
  const wb = XLSX.utils.book_new();
  const sheetData: (string | number)[][] = [];

  // Build lookup maps
  const categoryNameById = new Map<string, string>(
    (lookup?.allCategories ?? []).map((c) => [c.id, c.name]),
  );
  const productNameById = new Map<string, string>(
    (lookup?.allProducts ?? []).map((p) => [p.id, p.name]),
  );
  // Build option name lookup from all options across all categories
  const optionNameById = new Map<string, string>();
  for (const { options } of categories) {
    for (const opt of options) {
      optionNameById.set(opt.dogma_configurationoptionsid, opt.dogma_name ?? '');
    }
  }

  const COLS = [
    '#',
    'Option Name',
    'Description',
    'Option Type',
    'Input Type',
    'Price Modifier',
    'Value',
    'Status',
    'Depends On',
    'Valid For Products',
  ];
  const COL_COUNT = COLS.length;

  // Track row indices for category/group headers
  const categoryHeaderRows: number[] = [];
  const groupHeaderRows: number[] = [];

  sheetData.push(COLS);

  let rowIndex = 1;
  let optionCounter = 1;

  for (const { categoryName, options } of categories) {
    // Category header row
    categoryHeaderRows.push(rowIndex);
    sheetData.push([categoryName, ...Array(COL_COUNT - 1).fill('')]);
    rowIndex++;

    if (!options || options.length === 0) {
      sheetData.push(['', '(no options)', ...Array(COL_COUNT - 2).fill('')]);
      rowIndex++;
    } else {
      // Group options by optionType in sorted order
      const grouped = new Map<number, IConfigOption[]>();
      for (const opt of options) {
        const type = opt.dogma_optiontype ?? 0;
        if (!grouped.has(type)) grouped.set(type, []);
        grouped.get(type)!.push(opt);
      }
      const sortedTypes = [...grouped.keys()].sort((a, b) => a - b);

      for (const type of sortedTypes) {
        // Group header row
        groupHeaderRows.push(rowIndex);
        sheetData.push(['', getOptionTypeLabel(type), ...Array(COL_COUNT - 2).fill('')]);
        rowIndex++;

        for (const opt of grouped.get(type)!) {
          const condition = parseCondition(opt.dogma_displaycondition);
          sheetData.push([
            optionCounter++,
            opt.dogma_name ?? '',
            opt.dogma_description ?? '',
            getOptionTypeLabel(opt.dogma_optiontype),
            getInputTypeLabel(opt.dogma_inputtype),
            opt.dogma_pricemodifier ?? 0,
            opt.dogma_value ?? '',
            'Active',
            formatDependsOn(condition, categoryNameById, optionNameById),
            formatValidForProducts(condition, productNameById),
          ]);
          rowIndex++;
        }
      }
    }

    // Blank separator between categories
    sheetData.push(Array(COL_COUNT).fill(''));
    rowIndex++;
  }

  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  // Column widths
  ws['!cols'] = [
    { wch: 5 },   // #
    { wch: 35 },  // Option Name
    { wch: 40 },  // Description
    { wch: 30 },  // Option Type
    { wch: 28 },  // Input Type
    { wch: 16 },  // Price Modifier
    { wch: 20 },  // Value
    { wch: 10 },  // Status
    { wch: 55 },  // Depends On
    { wch: 40 },  // Valid For Products
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Configuration Options');

  const now = new Date();
  const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
  XLSX.writeFile(wb, `configuration_options_${timestamp}.xlsx`);
};
